import { createContext, type MutableRefObject, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { CheckCuitAvailabilityResponse } from '/@/hooks/nodes';
import type { UserType } from '/@/hooks/session/profiles';
import type {
    GrowerCreationEntrySource,
    GrowerCreationExitAction,
    GrowerCreationStep,
    GrowerExistsCase,
    GrowerIdentifierFormatError,
    GrowerMissingField,
} from '/@/store/tracking/events/growers';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { errorDetail, secondsSince } from '/@/views/activities/flow-tracking';

/**
 * One pass through the "Agregar productor" modal, from opening it to leaving it. The modal starts
 * it and ends it; the sections inside fire their events against it and publish what `flow:Ended`
 * reports (step, identifier length, missing fields), since they are unmounted by the time it fires.
 */
export type GrowerCreationFlow = {
    flowId: string;
    userProfile: UserType;
    /** When the identifier step began for the grower being added: the pass start, or the last grower added. */
    stepStartedAt: number;
    /** Validations for the grower being added. Reset once it is created or linked. */
    attempts: number;
    everValidated: boolean;
    growersCreated: number;
    growersLinked: number;
    ended: boolean;
    step: GrowerCreationStep;
    identifierLength: number;
    missingFields: GrowerMissingField[];
};

export const GrowerCreationFlowContext = createContext<MutableRefObject<GrowerCreationFlow | null> | null>(null);

/**
 * Holder of the current pass, or null outside the modal. Read `.current` when the event happens:
 * the modal starts the pass in an effect, after its children have rendered. Every tracker below
 * ignores a null flow.
 */
export const useGrowerCreationFlowRef = () => useContext(GrowerCreationFlowContext);

type Flow = GrowerCreationFlow | null | undefined;

/** The properties every event of the pass carries. */
export const flowEventProps = ({ flowId, userProfile }: GrowerCreationFlow) => ({
    flow_id: flowId,
    user_profile: userProfile,
});

export const startGrowerCreationFlow = (
    entrySource: GrowerCreationEntrySource,
    singleGrowerMode: boolean,
    userProfile: UserType
): GrowerCreationFlow => {
    const flow: GrowerCreationFlow = {
        flowId: uuidv4(),
        userProfile,
        stepStartedAt: Date.now(),
        attempts: 0,
        everValidated: false,
        growersCreated: 0,
        growersLinked: 0,
        ended: false,
        step: 'identifier',
        identifierLength: 0,
        missingFields: [],
    };
    mpTrackEvent('Grower Creation flow:Started', {
        ...flowEventProps(flow),
        entry_source: entrySource,
        single_grower_mode: singleGrowerMode,
    });
    return flow;
};

export type IdentifierValidationOutcome =
    | { result: 'format_error'; formatError: GrowerIdentifierFormatError }
    | { result: 'connection_error' }
    | { result: 'available' }
    | { result: 'already_exists'; existsCase: GrowerExistsCase; alertShown: boolean };

export const trackIdentifierValidated = (flow: Flow, outcome: IdentifierValidationOutcome) => {
    if (!flow) return;
    flow.attempts += 1;
    if (outcome.result === 'available' || outcome.result === 'already_exists') flow.everValidated = true;
    mpTrackEvent('Grower Identifier Validated', {
        ...flowEventProps(flow),
        result: outcome.result,
        ...(outcome.result === 'format_error' && { format_error: outcome.formatError }),
        ...(outcome.result === 'already_exists' && { exists_case: outcome.existsCase }),
        attempt_number: flow.attempts,
        alert_shown: outcome.result === 'already_exists' && outcome.alertShown,
        seconds_in_step: secondsSince(flow.stepStartedAt),
    });
};

export const trackIdentifierChanged = (flow: Flow, previousResult: 'available' | 'already_exists') => {
    if (!flow) return;
    mpTrackEvent('Grower Identifier Changed', { ...flowEventProps(flow), previous_result: previousResult });
};

/** The next grower starts from scratch: its own attempts and its own time in the identifier step. */
const startNextGrower = (flow: GrowerCreationFlow) => {
    flow.attempts = 0;
    flow.stepStartedAt = Date.now();
};

export const trackLinkRequestSent = (flow: Flow, target: 'rv' | 'advisor') => {
    if (!flow) return;
    flow.growersLinked += 1;
    mpTrackEvent('Grower Link Request Sent', { ...flowEventProps(flow), target });
    startNextGrower(flow);
};

export const trackGrowerCreated = (flow: Flow) => {
    if (!flow) return;
    flow.growersCreated += 1;
    mpTrackEvent('Grower Created', {
        ...flowEventProps(flow),
        attempts_to_valid: flow.attempts,
        growers_in_batch: flow.growersCreated,
    });
    startNextGrower(flow);
};

export const trackGrowerSaveFailed = (flow: Flow, error: unknown) => {
    if (!flow) return;
    mpTrackEvent('Grower Save Failed', { ...flowEventProps(flow), error_detail: errorDetail(error) });
};

/** Reports how the pass ended, once: later exits of the same pass (e.g. closing after "Agregar los lotes") are ignored. */
export const endGrowerCreationFlow = (flow: Flow, exitAction: GrowerCreationExitAction) => {
    if (!flow || flow.ended) return;
    flow.ended = true;
    mpTrackEvent('Grower Creation flow:Ended', {
        ...flowEventProps(flow),
        exit_action: exitAction,
        growers_created: flow.growersCreated,
        growers_linked: flow.growersLinked,
        last_step: flow.step,
        ever_validated: flow.everValidated,
        identifier_digits: flow.identifierLength,
        ...(flow.step === 'grower_data' && { missing_fields: flow.missingFields }),
    });
};

/** The required grower details still missing, in the order the form shows them. Mirrors `requiredFilled` in `useGrowerForm`. */
export const growerMissingFields = (
    values: { segment?: string; rv?: string; name?: string; location?: string; email?: string },
    { isAdvisor, isConsultant }: { isAdvisor: boolean; isConsultant: boolean }
): GrowerMissingField[] => {
    const missing: GrowerMissingField[] = [];
    if (isAdvisor && values.segment !== 'premium' && values.segment !== 'common') missing.push('segment');
    if (isConsultant && !values.rv) missing.push('rv');
    if (!values.name?.trim()) missing.push('name');
    if (!values.location?.trim()) missing.push('location');
    if (!values.email?.trim()) missing.push('email');
    return missing;
};

/**
 * Whether `CuitAlert` shows anything for an identifier that is already registered. It renders
 * nothing when the grower belongs to another RV or advisor and the user can't act on it: an RV
 * without a contact to reach, or a profile that is neither advisor nor RV.
 */
export const growerExistsAlertShown = (
    data: Exclude<CheckCuitAvailabilityResponse, { status: 'available' }>,
    { isAdvisor, isRV }: { isAdvisor: boolean; isRV: boolean }
): boolean => {
    switch (data.status) {
        case 'assigned_to_another_rv':
            return isAdvisor || (isRV && !!data.contact);
        case 'assigned_to_another_advisor':
            return isAdvisor || (isRV && !!data.contact);
        default:
            return true;
    }
};
